import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
// eslint-disable-next-line canonical/no-restricted-imports
import { m, type MotionProps } from 'framer-motion';
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  useMemo,
} from 'react';

// We should be able to discard this component when framer adds own implementation
// https://github.com/framer/motion/issues/1604
const useAnimatedProps = ({ ...props }) => {
  const { playwrightTest } = useAppConfiguration();

  return useMemo(() => {
    return playwrightTest
      ? {
          ...props,
          animate: undefined,
          initial: undefined,
          transition: undefined,
          variants: undefined,
          whileDrag: undefined,
          whileFocus: undefined,
          whileHover: undefined,
          whileInView: undefined,
          whileTap: undefined,
        }
      : props;
  }, [playwrightTest, props]);
};

export type BaseProps<T extends React.ElementType> = MotionProps &
  Omit<ComponentPropsWithoutRef<T>, 'style'>;

const Div = forwardRef(
  ({ ...props }: BaseProps<'div'>, ref: ForwardedRef<HTMLDivElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.div
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Span = forwardRef(
  ({ ...props }: BaseProps<'span'>, ref: ForwardedRef<HTMLSpanElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.span
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Button = forwardRef(
  ({ ...props }: BaseProps<'button'>, ref: ForwardedRef<HTMLButtonElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.button
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Path = forwardRef(
  ({ ...props }: BaseProps<'path'>, ref: ForwardedRef<SVGPathElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.path
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Svg = forwardRef(
  ({ ...props }: BaseProps<'svg'>, ref: ForwardedRef<SVGSVGElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.svg
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Ol = forwardRef(
  ({ ...props }: BaseProps<'ol'>, ref: ForwardedRef<HTMLOListElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.ol
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Anchor = forwardRef(
  ({ ...props }: BaseProps<'a'>, ref: ForwardedRef<HTMLAnchorElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.a
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const TextArea = forwardRef(
  ({ ...props }: BaseProps<'a'>, ref: ForwardedRef<HTMLTextAreaElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.textarea
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

const Paragraph = forwardRef(
  ({ ...props }: BaseProps<'p'>, ref: ForwardedRef<HTMLParagraphElement>) => {
    const animatedProps = useAnimatedProps(props);
    return (
      <m.p
        ref={ref}
        {...animatedProps}
      />
    );
  },
);

export const animated = {
  a: Anchor,
  button: Button,
  div: Div,
  ol: Ol,
  // eslint-disable-next-line id-length
  p: Paragraph,
  path: Path,
  span: Span,
  svg: Svg,
  textarea: TextArea,
};
